import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import { ReactElement } from "react";

import {
  PromptTemplate,
  PromptTemplateCreationParams
} from "../../../types/prompt-template";
import { DRAWER_PAPER_SX } from "../constants";
import useLensEditor from "../hooks/useLensEditor";
import { LensFormErrors } from "../utils";
import MetadataFormSection from "./MetadataFormSection";
import ModelConfigFormSection from "./ModelConfigFormSection";

const styles = {
  editorHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 3,
  },
  formActions: {
    mt: 4,
    display: "flex",
    justifyContent: "flex-end",
    gap: 2,
  },
} as const;

interface LensEditorProps {
  open: boolean;
  onClose: () => void;
  template: PromptTemplate | null;
  defaultTemplate: PromptTemplateCreationParams;
  onRefetch: () => void;
}

const LensEditor = ({
  open, onClose, template, defaultTemplate, onRefetch
}: LensEditorProps): ReactElement => {
  const {
    header, action, showTemplateInfo,
    formData, formErrors, loading, availableModels,
    onFieldChange, onMetadataChange, onModelConfigChange, onScopeChange, onSubmit
  } = useLensEditor({ template, defaultTemplate, onRefetch, onClose });

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: DRAWER_PAPER_SX }}>
      <EditorHeader title={header} onClose={onClose} />
      {showTemplateInfo && template && <TemplateInfo template={template} />}
      <BasicFieldsSection formData={formData} formErrors={formErrors} onFieldChange={onFieldChange} />
      <Grid container spacing={2}>
        <MetadataFormSection formData={formData} formErrors={formErrors} onMetadataChange={onMetadataChange} onScopeChange={onScopeChange} />
        <ModelConfigFormSection formData={formData} formErrors={formErrors} availableModels={availableModels} onModelConfigChange={onModelConfigChange} />
        <PromptFieldSection formData={formData} formErrors={formErrors} onFieldChange={onFieldChange} />
      </Grid>
      <FormActions actionLabel={action} loading={loading} onSubmit={onSubmit} onClose={onClose} />
    </Drawer>
  );
};

interface EditorHeaderProps {
  title: string;
  onClose: () => void;
}

const EditorHeader = ({ title, onClose }: EditorHeaderProps): ReactElement => (
  <Box sx={styles.editorHeader}>
    <Typography variant="h5">
      {title}
    </Typography>
    <IconButton onClick={onClose} aria-label="close">
      <CloseIcon />
    </IconButton>
  </Box>
);

interface TemplateInfoProps {
  template: PromptTemplate;
}

const TemplateInfo = ({ template }: TemplateInfoProps): ReactElement => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      ID: {template.id}
    </Typography>
    <Typography variant="caption" color="text.secondary" display="block">
      Created: {new Date(template.created).toLocaleString()}
    </Typography>
    <Typography variant="caption" color="text.secondary" display="block">
      Modified: {new Date(template.modified).toLocaleString()}
    </Typography>
  </Box>
);

interface BasicFieldsSectionProps {
  formData: PromptTemplateCreationParams;
  formErrors: LensFormErrors;
  onFieldChange: (field: string, value: string | number | boolean) => void;
}

const BasicFieldsSection = ({ formData, formErrors, onFieldChange }: BasicFieldsSectionProps): ReactElement => (
  <Grid container spacing={2}>
    <Grid size={{ xs: 12 }}>
      <TextField
        fullWidth label="Title" value={formData.title}
        onChange={(e): void => onFieldChange("title", e.target.value)}
        error={!!formErrors.title} helperText={formErrors.title} required
      />
    </Grid>
    <Grid size={{ xs: 12 }}>
      <TextField
        fullWidth label="Description" value={formData.description}
        onChange={(e): void => onFieldChange("description", e.target.value)}
        error={!!formErrors.description} helperText={formErrors.description}
        multiline rows={2} required
      />
    </Grid>
    <Grid size={{ xs: 12 }}>
      <FormControlLabel
        control={
          <Switch checked={formData.enabled} onChange={(e): void => onFieldChange("enabled", e.target.checked)} color="primary" />
        }
        label="Enabled"
      />
    </Grid>
  </Grid>
);

interface PromptFieldSectionProps {
  formData: PromptTemplateCreationParams;
  formErrors: LensFormErrors;
  onFieldChange: (field: string, value: string) => void;
}

const PromptFieldSection = ({ formData, formErrors, onFieldChange }: PromptFieldSectionProps): ReactElement => (
  <>
    <Grid size={{ xs: 12 }}>
      <Divider sx={{ my: 2 }} />
      <Typography variant="h6" sx={{ mb: 2 }}>Prompt Template</Typography>
    </Grid>
    <Grid size={{ xs: 12 }}>
      <TextField
        fullWidth label="Prompt" value={formData.prompt}
        onChange={(e): void => onFieldChange("prompt", e.target.value)}
        error={!!formErrors.prompt}
        helperText={formErrors.prompt || "The prompt template that will be used. You can use variables like {summary}, {transcript}, etc."}
        multiline rows={25} required
      />
    </Grid>
  </>
);

interface FormActionsProps {
  actionLabel: string;
  loading: boolean;
  onSubmit: () => void;
  onClose: () => void;
}

const FormActions = ({ actionLabel, loading, onSubmit, onClose }: FormActionsProps): ReactElement => (
  <Box sx={styles.formActions}>
    <Button onClick={onClose} disabled={loading}>Cancel</Button>
    <Button
      variant="contained" color="primary" onClick={onSubmit} disabled={loading}
      startIcon={loading && <CircularProgress size={20} color="inherit" />}
    >
      {actionLabel}
    </Button>
  </Box>
);

export default LensEditor;
